import { useActiveFleet } from '@/data/fleets';
import { useBilling, useCheckoutSession, type BillingState } from '@/data/billing';
import { priceLine } from '@/pages/billing';
import { Brand } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { authService } from '@/auth/hooks';
import { fleetStore } from '@/lib/fleet-store';

/**
 * P2 card wall — full screen, no nav underneath. Shown by the Shell when
 * the trial has expired with no card (billing.wall). Adding a card creates
 * the subscription (honouring any trial remainder ≥48h, which by this point
 * there won't be) and the webhook flips the state — the wall clears itself
 * on the next billing refetch.
 */
export function BillingWall({ state }: { state: BillingState }) {
  const { fleet, role } = useActiveFleet();
  const checkout = useCheckoutSession(fleet?.id ?? null);
  const isOwner = role === 'owner';

  async function handleLogout() {
    await authService.logout();
    fleetStore.clear();
    window.location.assign('/login');
  }

  return (
    <div className="min-h-screen flex flex-col bg-breaker-ground">
      <div className="h-1 bg-breaker-identity" />
      <div className="flex-1 grid place-items-center px-6">
        <div className="max-w-md w-full rounded-xl border border-breaker-line bg-white p-8 text-center space-y-4">
          <div className="flex justify-center">
            <Brand size="md" />
          </div>
          <h1 className="text-xl font-semibold text-breaker-ink">Your trial has ended</h1>
          <p className="text-sm text-breaker-mute">
            Everything you set up is still here — add a card to keep dispatching.
            One plan, everything included: {state ? priceLine(state) : '£20 per driver / month'},
            minimum one driver.
          </p>
          {isOwner ? (
            <Button size="lg" action={() => checkout.mutateAsync().catch(() => undefined)}>
              Add a card
            </Button>
          ) : (
            <p className="text-sm text-breaker-ink-2">
              Ask your fleet owner to add a card from this screen.
            </p>
          )}
          {checkout.isError && (
            <p className="text-sm text-breaker-red">That didn&apos;t work — try again.</p>
          )}
          <button
            onClick={handleLogout}
            className="text-xs text-breaker-mute underline underline-offset-2"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The Shell's one hook into P2 gating. Returns which full-screen gate (if
 * any) applies to the active fleet right now.
 */
export function useBillingGate(): {
  gate: 'wall' | 'suspended' | null;
  state: BillingState | undefined;
} {
  const { fleet } = useActiveFleet();
  const billingQuery = useBilling(fleet?.id ?? null);
  const state = billingQuery.data;
  if (!state) return { gate: null, state: undefined };
  // 0084: a founding fleet never meets a money gate. The server already
  // returns wall:false for them; this is the client-side half of the same
  // promise, so a stale cached view can't flash a wall at them either.
  // An ADMIN suspension is deliberately still gated — that outranks the
  // exemption, and the server drives it through billingStatus.
  if (state.billing.exempt) {
    return state.billing.status === 'suspended'
      ? { gate: 'suspended', state }
      : { gate: null, state };
  }
  if (state.billing.wall) return { gate: 'wall', state };
  if (state.billing.status === 'suspended') return { gate: 'suspended', state };
  return { gate: null, state };
}
